import React, { type ReactElement, type ReactNode, useCallback, useMemo, useState } from 'react';

import { loadEntryPoint } from 'react-relay';
import { mergeRefs } from 'use-callback-ref';

import Button from '@atlaskit/button/new';
import DropdownMenu, {
	type DropdownMenuProps,
	type CustomTriggerProps as TriggerProps,
} from '@atlaskit/dropdown-menu';
import ExpandIcon from '@atlaskit/icon/core/migration/chevron-down';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import type {
	AnyEntryPoint,
	EntryPointRef,
	ParamsOfEntryPoint,
	RuntimePropsOfEntryPoint,
} from '@atlassian/entry-point-types';
import { usePressablePreloadRef } from '@atlassian/entry-point-use-trigger';
import {
	type ErrorCallback,
	type ErrorFallback,
	InternalEntryPointContainer,
} from '@atlassian/internal-entry-point-container';
import type { JSResourceReference } from '@atlassian/react-async';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
const emptyEntryPointParams = {} as ParamsOfEntryPoint<AnyEntryPoint>;
const emptyEntryPointProps = {} as RuntimePropsOfEntryPoint<AnyEntryPoint>;

type UnionOmit<T, K extends string | number | symbol> = T extends unknown ? Omit<T, K> : never;

type CustomDropdownProps = UnionOmit<
	DropdownMenuProps,
	'trigger' | 'handleOpenChange' | 'children'
>;

export type CustomTriggerProps = TriggerProps & { ref: TriggerProps['triggerRef'] };

export type Props<TEntryPoint> = {
	entryPoint: TEntryPoint;
	entryPointParams?: ParamsOfEntryPoint<TEntryPoint>;
	entryPointProps?: RuntimePropsOfEntryPoint<TEntryPoint>;
	errorFallback?: ErrorFallback;
	fallback?: ReactNode;
	onError?: ErrorCallback;
	trigger: string | ((triggerProps: CustomTriggerProps) => ReactElement);
} & CustomDropdownProps;

export function DropdownTrigger<TEntryPoint extends AnyEntryPoint>({
	trigger,
	entryPoint,
	entryPointParams,
	entryPointProps,
	fallback,
	errorFallback,
	onError,
	...dropdownProps
}: Props<TEntryPoint>) {
	const environmentProvider = useRelayEnvironmentProvider();

	const id = useMemo((): string => {
		return (entryPoint.root as JSResourceReference<any>).getModuleName() || 'unknown';
	}, [entryPoint]);

	const [isOpen, setIsOpen] = useState(false);
	const [children, setChildren] = useState<ReactNode | null>(null);

	const handleOpenChange = useCallback(({ isOpen }: { isOpen: boolean }) => {
		setIsOpen(isOpen);
	}, []);

	const load = useCallback(
		() =>
			loadEntryPoint<TEntryPoint>(
				environmentProvider,
				entryPoint,
				entryPointParams ?? emptyEntryPointParams,
			),
		[entryPoint, entryPointParams, environmentProvider],
	);

	const onLoad = useCallback(
		({ reference: entryPointReference }: { reference: EntryPointRef<TEntryPoint> }) => {
			// avoid re-loading entry-point when closing popup
			if (isOpen) {
				return;
			}

			setChildren(
				<InternalEntryPointContainer
					id={id}
					entryPointReference={entryPointReference}
					fallback={fallback || <LoadingState />}
					errorFallback={errorFallback}
					runtimeProps={entryPointProps || emptyEntryPointProps}
					onError={onError}
				/>,
			);
		},
		[setChildren, id, isOpen, entryPointProps, fallback, errorFallback, onError],
	);

	const triggerRef = usePressablePreloadRef<EntryPointRef<TEntryPoint>>({
		load,
		onLoad,
	});

	const popupTrigger = useCallback(
		(triggerProps: TriggerProps): ReactElement => {
			const mergedRef = mergeRefs([triggerRef, triggerProps.triggerRef]);

			if (typeof trigger === 'string') {
				return (
					<Button
						{...triggerProps}
						ref={mergedRef}
						isSelected={isOpen}
						iconAfter={(iconProps) => <ExpandIcon {...iconProps} size="small" />}
						testId={dropdownProps.testId && `${dropdownProps.testId}--trigger`}
						aria-label={dropdownProps.label}
					>
						{trigger}
					</Button>
				);
			}

			return trigger({ ...triggerProps, ref: mergedRef, triggerRef: mergedRef });
		},
		[trigger, triggerRef, isOpen, dropdownProps.label, dropdownProps.testId],
	);

	return (
		<DropdownMenu
			{...dropdownProps}
			onOpenChange={handleOpenChange}
			trigger={popupTrigger}
			shouldFitContainer={
				fg('should-render-to-parent-should-be-true-uip-applica') ? false : undefined
			}
			shouldRenderToParent={fg('should-render-to-parent-should-be-true-uip-applica')}
		>
			{children}
		</DropdownMenu>
	);
}

const spinnerContainerStyles = xcss({
	display: 'flex',
	minWidth: '160px',
	padding: 'space.250',
	justifyContent: 'center',
});

function LoadingState() {
	return (
		<Box xcss={spinnerContainerStyles}>
			<Spinner size="small" />
		</Box>
	);
}
